import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DollarSign, Users, Monitor, Banknote,
  ShieldAlert, AlertTriangle, Info, CheckCircle,
  ClipboardCheck, Snowflake, Siren, Activity,
  ChevronDown, ExternalLink,
} from 'lucide-react'
import {
  AreaChart, Area, XAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line,
} from 'recharts'
import { cn } from '@/lib/utils'
import { fadeInUp, cardReveal } from '@/lib/motion'

// -- Period-based mock data --

type PeriodKey = '今日' | '本週' | '本月'

interface MetricData {
  label: string
  sub: string
  value: string
  change: string | null
  changeType: 'green' | 'amber' | 'neutral'
  icon: typeof DollarSign
}

const metricsByPeriod: Record<PeriodKey, MetricData[]> = {
  '今日': [
    { label: '今日營收', sub: 'Today Revenue', value: '$1,847.50', change: '+12.3%', changeType: 'green', icon: DollarSign },
    { label: '活躍玩家', sub: 'Active Players', value: '47', change: '+5', changeType: 'green', icon: Users },
    { label: '活躍房間', sub: 'Active Rooms', value: '12', change: null, changeType: 'neutral', icon: Monitor },
    { label: '待審提款', sub: 'Pending Withdrawals', value: '3', change: '!', changeType: 'amber', icon: Banknote },
  ],
  '本週': [
    { label: '本週營收', sub: 'Week Revenue', value: '$12,847.50', change: '+8.7%', changeType: 'green', icon: DollarSign },
    { label: '活躍玩家', sub: 'Active Players', value: '134', change: '+22', changeType: 'green', icon: Users },
    { label: '活躍房間', sub: 'Active Rooms', value: '28', change: null, changeType: 'neutral', icon: Monitor },
    { label: '待審提款', sub: 'Pending Withdrawals', value: '7', change: '!', changeType: 'amber', icon: Banknote },
  ],
  '本月': [
    { label: '本月營收', sub: 'Month Revenue', value: '$48,320.00', change: '+15.1%', changeType: 'green', icon: DollarSign },
    { label: '活躍玩家', sub: 'Active Players', value: '312', change: '+67', changeType: 'green', icon: Users },
    { label: '活躍房間', sub: 'Active Rooms', value: '45', change: null, changeType: 'neutral', icon: Monitor },
    { label: '待審提款', sub: 'Pending Withdrawals', value: '12', change: '!', changeType: 'amber', icon: Banknote },
  ],
}

// Revenue trend chart data (numeric values)
const revenueChartByPeriod: Record<PeriodKey, { data: { label: string; value: number }[]; total: string }> = {
  '今日': {
    data: [
      { label: '6am', value: 800 },
      { label: '8am', value: 1200 },
      { label: '10am', value: 950 },
      { label: '12pm', value: 1500 },
      { label: '2pm', value: 1800 },
      { label: '4pm', value: 2100 },
      { label: '6pm', value: 1847 },
    ],
    total: '$1,847.50',
  },
  '本週': {
    data: [
      { label: '一', value: 1520 },
      { label: '二', value: 1890 },
      { label: '三', value: 1340 },
      { label: '四', value: 2100 },
      { label: '五', value: 1750 },
      { label: '六', value: 2400 },
      { label: '日', value: 1847 },
    ],
    total: '$12,847.50',
  },
  '本月': {
    data: [
      { label: 'W1', value: 9800 },
      { label: 'W2', value: 11200 },
      { label: 'W3', value: 10500 },
      { label: 'W4', value: 12400 },
      { label: 'W5', value: 11800 },
      { label: 'W6', value: 14200 },
      { label: 'W7', value: 13800 },
    ],
    total: '$48,320.00',
  },
}

// Player activity trend data
const playerTrendByPeriod: Record<PeriodKey, { label: string; count: number }[]> = {
  '今日': [
    { label: '6am', count: 12 },
    { label: '8am', count: 18 },
    { label: '10am', count: 25 },
    { label: '12pm', count: 34 },
    { label: '2pm', count: 42 },
    { label: '4pm', count: 47 },
    { label: '6pm', count: 38 },
  ],
  '本週': [
    { label: '一', count: 35 },
    { label: '二', count: 42 },
    { label: '三', count: 38 },
    { label: '四', count: 55 },
    { label: '五', count: 48 },
    { label: '六', count: 62 },
    { label: '日', count: 47 },
  ],
  '本月': [
    { label: 'W1', count: 180 },
    { label: 'W2', count: 220 },
    { label: 'W3', count: 245 },
    { label: 'W4', count: 280 },
    { label: 'W5', count: 310 },
    { label: 'W6', count: 290 },
    { label: 'W7', count: 312 },
  ],
}

// Revenue breakdown data (in cents)
const revenueBreakdownByPeriod: Record<PeriodKey, { rake: number; commission: number; fees: number; jackpot: number }> = {
  '今日': { rake: 120500, commission: 33650, fees: 15700, jackpot: 14900 },
  '本週': { rake: 842000, commission: 234000, fees: 108750, jackpot: 100000 },
  '本月': { rake: 3140800, commission: 869760, fees: 434880, jackpot: 386560 },
}

const DONUT_COLORS = {
  rake: '#d4a843',
  commission: '#8b5cf6',
  fees: '#06b6d4',
  jackpot: '#10b981',
}

const DONUT_LABELS: Record<string, string> = {
  rake: '抽水',
  commission: '佣金',
  fees: '手續費',
  jackpot: '彩池',
}

// -- Alert data with expanded details --

interface Alert {
  severity: string
  icon: typeof ShieldAlert
  title: string
  desc: string
  time: string
  fullDesc: string
  affected: string
  navigateTo: string
}

const alerts: Alert[] = [
  {
    severity: 'red', icon: ShieldAlert, title: '可疑登入偵測',
    desc: 'Player_9201 從新 IP 登入並立即大額提款', time: '2 分鐘前',
    fullDesc: '用戶 Player_9201 從未知 IP 203.45.67.89 登入後，30 秒內發起 $5,000 提款請求。行為模式與過去記錄不符，觸發自動風控標記。',
    affected: '用戶 Player_9201', navigateTo: '/admin/fraud',
  },
  {
    severity: 'amber', icon: AlertTriangle, title: '提款審核逾時',
    desc: '提款 #W-4821 已超過 24 小時未審核', time: '15 分鐘前',
    fullDesc: '提款 #W-4821 金額 $2,500，由用戶 大衛王 於昨日 13:45 提交，已超過 SLA 24 小時審核時限。',
    affected: '用戶 大衛王 / 提款 #W-4821', navigateTo: '/admin/withdrawals',
  },
  {
    severity: 'blue', icon: Info, title: 'Redis 延遲升高',
    desc: '平均延遲 45ms，超過正常閾值 (< 20ms)', time: '32 分鐘前',
    fullDesc: 'Redis 快取服務延遲從正常 12ms 升至 45ms，持續約 20 分鐘。可能受到大量玩家同時操作影響。記憶體使用率 72%。',
    affected: 'Redis 快取服務', navigateTo: '/admin/health',
  },
  {
    severity: 'green', icon: CheckCircle, title: '房間 #R005 已開局',
    desc: 'Agent_Lin 建立 2/5 級別房間，6 位玩家入座', time: '1 小時前',
    fullDesc: '代理 Agent_Lin 開設 2/5 盲注私人房間 R-005，目前 6/9 座位已滿。預計抽水 $120/小時。',
    affected: '房間 R-005 / 代理 Agent_Lin', navigateTo: '/admin/agents',
  },
  {
    severity: 'red', icon: ShieldAlert, title: '彩池分配異常',
    desc: 'Hand #H-29441 兩庫比對不一致，彩池已凍結', time: '2 小時前',
    fullDesc: 'Hand #H-29441 pokersolver 與 poker-evaluator 結果不一致。彩池金額 $340 已自動凍結，等待管理員手動審核。',
    affected: '手牌 #H-29441 / 房間 R-003', navigateTo: '/admin/frozen-pots',
  },
]

const severityColor: Record<string, string> = {
  red: 'bg-red-500', amber: 'bg-amber-500', blue: 'bg-blue-500', green: 'bg-emerald-500',
}
const severityBg: Record<string, string> = {
  red: 'bg-red-500/10', amber: 'bg-amber-500/10', blue: 'bg-blue-500/10', green: 'bg-emerald-500/10',
}
const severityText: Record<string, string> = {
  red: 'text-red-400', amber: 'text-amber-400', blue: 'text-blue-400', green: 'text-emerald-400',
}

const quickActions = [
  { label: '審核提款', icon: ClipboardCheck, color: 'text-amber-400 bg-amber-500/10', route: '/admin/withdrawals' },
  { label: '凍結彩池', icon: Snowflake, color: 'text-blue-400 bg-blue-500/10', route: '/admin/frozen-pots' },
  { label: '詐欺警報', icon: Siren, color: 'text-red-400 bg-red-500/10', route: '/admin/fraud' },
  { label: '系統狀態', icon: Activity, color: 'text-emerald-400 bg-emerald-500/10', route: '/admin/health' },
]

const periods: PeriodKey[] = ['今日', '本週', '本月']

// -- Custom tooltip for charts --

function RevenueTooltipContent({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg bg-surface-elevated border border-foreground/10">
      <p className="text-muted-foreground mb-0.5">{label}</p>
      <p className="text-foreground font-bold font-mono">${payload[0].value.toLocaleString()}</p>
    </div>
  )
}

function PlayerTooltipContent({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg bg-surface-elevated border border-foreground/10">
      <p className="text-muted-foreground mb-0.5">{label}</p>
      <p className="text-foreground font-bold font-mono">{payload[0].value} players</p>
    </div>
  )
}

export function AdminDashboardScreen() {
  const [period, setPeriod] = useState<PeriodKey>('本週')
  const [expandedAlert, setExpandedAlert] = useState<number | null>(null)
  const navigate = useNavigate()

  const metrics = metricsByPeriod[period]
  const revenueChart = revenueChartByPeriod[period]
  const playerTrend = playerTrendByPeriod[period]
  const breakdown = revenueBreakdownByPeriod[period]

  // Build donut chart data
  const donutData = useMemo(() => {
    const entries = [
      { name: 'rake', value: breakdown.rake },
      { name: 'commission', value: breakdown.commission },
      { name: 'fees', value: breakdown.fees },
      { name: 'jackpot', value: breakdown.jackpot },
    ]
    const total = entries.reduce((s, e) => s + e.value, 0)
    return { entries, total }
  }, [breakdown])

  const formatCents = (cents: number) => {
    return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-8 flex flex-col gap-5"
    >
      {/* Key Metrics */}
      <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3">
        {metrics.map((m) => (
          <motion.div key={m.label} variants={cardReveal} className="p-3.5 rounded-2xl glass-card space-y-2">
            <div className="flex items-center justify-between">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center',
                m.changeType === 'amber' ? 'bg-amber-500/10 text-amber-400' : 'bg-foreground/5 text-muted-foreground'
              )}>
                <m.icon className="w-4 h-4" />
              </div>
              {m.change && (
                <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-md',
                  m.changeType === 'green' && 'bg-emerald-500/10 text-emerald-400',
                  m.changeType === 'amber' && 'bg-amber-500/10 text-amber-400',
                )}>
                  {m.change}
                </span>
              )}
            </div>
            <div>
              <p className="text-xl font-bold font-mono text-foreground">{m.value}</p>
              <p className="text-[10px] text-muted-foreground">{m.label}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Online Players Trend */}
      <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card">
        <h3 className="text-sm font-bold text-foreground mb-3">線上玩家趨勢</h3>
        <div className="h-16">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={playerTrend} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <RechartsTooltip content={<PlayerTooltipContent />} cursor={false} />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ r: 3, fill: '#10b981', stroke: '#10b981' }}
                activeDot={{ r: 5, fill: '#10b981', stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </motion.section>

      {/* Revenue Breakdown Donut */}
      <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card">
        <h3 className="text-sm font-bold text-foreground mb-3">營收組成</h3>
        <div className="flex items-center gap-4">
          <div className="relative w-32 h-32 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData.entries}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={36}
                  outerRadius={56}
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={2}
                  stroke="none"
                >
                  {donutData.entries.map((entry) => (
                    <Cell key={entry.name} fill={DONUT_COLORS[entry.name as keyof typeof DONUT_COLORS]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            {/* Center total */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[9px] text-muted-foreground">總計</span>
              <span className="text-sm font-bold font-mono text-foreground">{formatCents(donutData.total)}</span>
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-col gap-2.5 flex-1 min-w-0">
            {donutData.entries.map((entry) => {
              const pct = ((entry.value / donutData.total) * 100).toFixed(1)
              return (
                <div key={entry.name} className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: DONUT_COLORS[entry.name as keyof typeof DONUT_COLORS] }}
                  />
                  <span className="text-[11px] text-muted-foreground flex-1 truncate">
                    {DONUT_LABELS[entry.name]}
                  </span>
                  <span className="text-[11px] font-mono font-bold text-foreground">
                    {formatCents(entry.value)}
                  </span>
                  <span className="text-[10px] text-muted-foreground/60 w-10 text-right">
                    {pct}%
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </motion.section>

      {/* Revenue Trend */}
      <motion.section variants={fadeInUp} className="p-4 rounded-2xl glass-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-foreground">營收趨勢</h3>
          <div className="flex gap-1 bg-surface-elevated rounded-lg p-0.5">
            {periods.map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={cn('px-2.5 py-1 rounded-md text-[10px] font-bold transition-colors',
                  period === p ? 'bg-gold/20 text-gold' : 'text-muted-foreground'
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <div className="h-36 mb-3">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueChart.data} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
              <defs>
                <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#d4a843" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#d4a843" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                dy={8}
              />
              <RechartsTooltip content={<RevenueTooltipContent />} cursor={false} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#d4a843"
                strokeWidth={2}
                fill="url(#goldGradient)"
                activeDot={{ r: 5, fill: '#d4a843', stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-between border-t border-foreground/10 pt-3">
          <span className="text-xs text-muted-foreground">期間總計</span>
          <span className="text-lg font-bold font-mono text-gradient-gold">{revenueChart.total}</span>
        </div>
      </motion.section>

      {/* Recent Alerts */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">最新警報</h3>
        {alerts.map((a, i) => {
          const isExpanded = expandedAlert === i
          return (
            <motion.div key={i} variants={cardReveal} className="rounded-xl glass-card overflow-hidden">
              <button
                onClick={() => setExpandedAlert(isExpanded ? null : i)}
                className="w-full text-left flex gap-3 p-3"
              >
                <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', severityBg[a.severity])}>
                  <a.icon className={cn('w-4 h-4', severityText[a.severity])} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', severityColor[a.severity])} />
                    <p className="text-xs font-bold text-foreground truncate">{a.title}</p>
                  </div>
                  <p className="text-[10px] text-muted-foreground line-clamp-1">{a.desc}</p>
                  <p className="text-[9px] text-muted-foreground/60 mt-1">{a.time}</p>
                </div>
                <ChevronDown className={cn(
                  'w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 transition-transform duration-200',
                  isExpanded && 'rotate-180'
                )} />
              </button>
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-3 pb-3 pt-0 border-t border-foreground/10 mx-3 space-y-2">
                      <p className="text-[11px] text-foreground/80 leading-relaxed pt-2">{a.fullDesc}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">影響對象:</span>
                        <span className="text-[10px] text-foreground font-bold">{a.affected}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(a.navigateTo)
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gold/10 text-gold text-[11px] font-bold active:scale-95 transition-transform"
                      >
                        <ExternalLink className="w-3 h-3" />
                        前往處理
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )
        })}
      </motion.section>

      {/* Quick Actions */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">快捷操作</h3>
        <div className="grid grid-cols-4 gap-2">
          {quickActions.map((a) => (
            <button
              key={a.label}
              onClick={() => navigate(a.route)}
              className="flex flex-col items-center gap-2 p-3 rounded-xl glass-card active:scale-95 transition-transform"
            >
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', a.color)}>
                <a.icon className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-bold text-muted-foreground leading-tight text-center">{a.label}</span>
            </button>
          ))}
        </div>
      </motion.section>
    </motion.div>
  )
}
